/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import * as emotion from "emotion";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import {
	AlertFeedback,
	AlertStatus,
	CommentMap,
	DB_Comment,
	FeedbackCategory,
	StatusChange
} from "@app/app-shared/alert";
import dayjs from "../../../utils/dayjs";
import {FONTS} from "@res/fonts";
import {formatAlertDatetime} from "./util";
import {HGap} from "../../widget/HGap";
import {VGap} from "../../widget/VGap";
import {extractNameFromEmail} from "@app/ir-q-app-common/types/care-center/user";
import {_keys} from "@intuitionrobotics/ts-common";
import {COLORS} from "@res/colors";
import {AlertsModule} from "@modules/AlertsModule";
import {UsersModule} from "@modules/UsersModule";
import {DeleteComment} from "./DeleteComment";
import {getUserOrgRoles} from "@modules/utils";
import {ID_ALL, OrgRoleLevel} from "@app/app-shared/permission";

type Props = {
	comment: DB_Comment<keyof CommentMap>;
    organizationId: string
}

const dot = emotion.css(
	{
		width: 8,
		height: 8,
		backgroundColor: COLORS.black,
		borderRadius: "50%"
	}
);

export class CommentRow
	extends BaseComponent<Props, {}> {

	constructor(props: Props) {
		super(props);
	}

	private prettyLabel = (label: string) => {
		switch (label) {
			case AlertStatus.OPEN:
			case AlertStatus.NEW:
				return "Open";
			case AlertStatus.COMPLETE:
				return "Complete";
			case AlertStatus.IN_PROGRESS:
				return "In progress";
			default:
				return label;
		}
	};

	renderCommentHeader = () => {
		const timestamp = this.props.comment.timestamp;
		const {date} = formatAlertDatetime(timestamp.auditAt.timestamp);
		const time = dayjs(timestamp.auditAt.timestamp).format("hh:mm A");
		const currUser = UsersModule.getCurrentUserByEmail();
        const userOrgRoles = getUserOrgRoles();
        const userOrgRole = userOrgRoles[ID_ALL] || userOrgRoles[this.props.organizationId] || [];
		const isAbleToDelete = currUser?.mail === timestamp.auditBy || userOrgRole.find(role => role === OrgRoleLevel.USER_DATA);

		return <div className={"ll_h_r"}>
			<div style={{width: "-webkit-fill-available"}}>
				{FONTS.medium_14(extractNameFromEmail(timestamp.auditBy))}
				<HGap h={10}/>
				<div style={{display: "flex", justifyContent: "space-between"}}>
					{FONTS.reg_11(`${date} | ${time}`, {color: COLORS.neutral_text_dark})}
					{isAbleToDelete && this.props.comment.type !== "statusChange" && this.props.comment.type !== "feedback" &&
					<div onClick={() => DeleteComment.show(this.props.comment._id)}>{FONTS.reg_12("Delete",
					                                                                              {textDecoration: "underline", color: "#53bad5", cursor: "pointer"})}
					</div>}
				</div>
			</div>
		</div>;
	};

	render() {
		const comment = this.props.comment;
		if (comment.type === "statusChange") {
			const fromStatus = this.prettyLabel((comment.data as StatusChange).from);
			const toStatus = this.prettyLabel((comment.data as StatusChange).to);

			if (fromStatus === toStatus)
				return null
		}

		return <div style={{margin: "20px", wordBreak: "break-word", boxSizing: "border-box"}}>
			{this.renderCommentHeader()}
			<VGap v={8}/>
			<div>
				{this.getPresentation()}
			</div>
		</div>;
	}

	private getPresentation = (): string | React.ReactNode => {
		const comment = this.props.comment;
		switch (comment.type) {
			case "text":
				return this.renderString(comment.data as string);
			case "statusChange":
				const data = comment.data as StatusChange;
				return this.renderStatusChange(data);
			case "feedback":
				return this.renderFeedbackComment(comment.data as AlertFeedback);
			default:
				// Hack till we migrate...
				// @ts-ignore
				return this.renderString(comment.text as string);
		}
	};

	private renderString = (comment: string) => {
		return FONTS.reg_14(comment);
	};


	private renderStatusChange = (data: StatusChange) => {
		// Any better away to format this?
		const fromStatus = this.prettyLabel(data.from);
		const toStatus = this.prettyLabel(data.to);

		return <div>
			<pre>
			{
				FONTS.reg_11(`Status changed`,
				             {color: "rgb(0,0,0,0.85)"})
			}
				{data.from && <>
					{
						FONTS.reg_11(` from "`,
						             {color: "rgb(0,0,0,0.85)"})
					}
					{
						FONTS.reg_11_bold(fromStatus,
						                  {color: "rgb(0,0,0,0.85)"})
					}
					{
						FONTS.reg_11(`"`,
						             {color: "rgb(0,0,0,0.85)"})
					}
				</>}
				{
					FONTS.reg_11(` to "`,
					             {color: "rgb(0,0,0,0.85)"})
				}
				{
					FONTS.reg_11_bold(toStatus,
					                  {color: "rgb(0,0,0,0.85)"})
				}
				{
					FONTS.reg_11(`"`,
					             {color: "rgb(0,0,0,0.85)"})
				}
			</pre>
		</div>;
	};

	private renderFeedbackComment = (feedback: AlertFeedback) => {
		if (!feedback || !_keys(feedback).length)
			return null;

		return <div className={"ll_v_l"}>
			<div style={{marginBottom: 10}}>
				{
					FONTS.semi_bold_14("Action taken", {color: COLORS.black})
				}
			</div>
			{this.renderFeedback(feedback)}
		</div>;
	};

	private renderFeedback = (feedback: AlertFeedback) => {
		const feedbackCategories: FeedbackCategory[] = AlertsModule.getFeedbackCategories();
		const displayedFeedback: { categoryLabel: string, key: string, label: string }[] = [];

		// The feedback should preserve the order they have in the dialog.
		for (const category of feedbackCategories) {
			const key = category.key;
			const userFeedbackOptions = feedback[key];
			if (!userFeedbackOptions)
				continue;

			for (const option of category.options) {
				if (userFeedbackOptions.includes(option.key))
					displayedFeedback.push({categoryLabel: category.label, key: option.key, label: option.label});
			}
		}
		if (feedback["free-text"]?.length)
			displayedFeedback.push({categoryLabel: "Other", key: "other", label: feedback["free-text"][0]});

		return <div className={"ll_v_l"}>
			{
				displayedFeedback.map((fb, idx) => {
					const display = `${fb.categoryLabel} | ${fb.label}`;
					return <div key={fb.key}
					            className={"ll_h_t"}
					            style={{
						            marginBottom: (idx !== displayedFeedback.length - 1) ? 8 : 0
					            }}>
						<div style={{marginTop: 3, marginRight: 10}}>
							<div className={dot}/>
						</div>
						<div className={"ll_h_c"}>
							{
								FONTS.reg_12(display)
							}
						</div>
					</div>;
				})
			}
		</div>;
	}
	;

}
